import { useRef } from "react";
import * as _ from "lodash";
import { ColumnConfig, Datum } from "@ant-design/charts";
import { useDeepCompareCallback, useDeepCompareEffect, useDeepCompareMemo } from "use-deep-compare";

import { ChartSeriesType, ChartType, ColumnData, DataPointsMeta } from "@ctra/api";
import { Units, hslToHex, isDebugMode } from "@ctra/utils";
import { Enterprise as Content, useTranslation } from "@ctra/i18n";

import { useChartContext, useSeriesColor } from "../../../providers";
import { useCommonConfig } from "../../../hooks";
import { formatXLabel, getStackedMax } from "../../../utils";
import { SingleEntityColumnChartProcessor } from "./processing/Individual";
import { MultiEntityColumnChartProcessor } from "./processing/Combined";
import { Processor } from "./processing/Factory";

import "../Tooltip.css";

const {
  kpi: { displayName }
} = Content;

/**
 * Hook that merges column config with the common config
 * The common config coming from the main app overrides the preset config
 * @returns
 */
export const useColumnConfig = (): { config: ColumnConfig } => {
  const { commonConfig } = useCommonConfig();
  const chartContext = useChartContext<ColumnData>();
  const { t } = useTranslation();

  const {
    paletteType,
    api: { getColor, setPaletteType }
  } = useSeriesColor();

  const {
    values: {
      entities,
      series: s,
      data: d,
      meta: m,
      config: contextConfig,
      dataOptions: { bucketSize }
    },
    api: { setConsolidatedSeries, setConsolidatedData }
  } = chartContext;

  /**
   * Create a ref for holding the chart config utility
   * @type {React.MutableRefObject<Processor>}
   */
  const processor = useRef<SingleEntityColumnChartProcessor | MultiEntityColumnChartProcessor>(
    Processor.create(entities)
  );

  /**
   * Recreate the processor when the entities change
   */
  useDeepCompareEffect(() => {
    processor.current = Processor.create(entities);
  }, [entities]);

  /**
   * Get the series from the processor
   * @type {SeriesType}
   */
  const series = useDeepCompareMemo(
    () => processor.current.getSeries(m, s),
    [m, s, processor.current.identity]
  );

  /**
   * Update the values for use in other context depending on the data
   */
  useDeepCompareEffect(() => {
    setConsolidatedSeries(series);
  }, [series]);

  /**
   * Get the data from the processor
   * @type {Array<{x: number, y: string, seriesField: string, context?: PointContext}>}
   */
  const data = useDeepCompareMemo(
    () => processor.current.getData(d, m, s),
    [d, m, s, processor.current.identity]
  );

  /**
   * Update the data for use in other context depending on the data
   */
  useDeepCompareEffect(() => {
    setConsolidatedData(data);
  }, [data]);

  /**
   * Tell if the chart has overlays.
   * Multi-type and multi-farm is considered a factor, not the number of entities.
   * This way we can consider overlays "just grouped bars".
   * @type {boolean}
   */
  const hasOverlays = useDeepCompareMemo(() => {
    const seriesTypes = _.map(m, "seriesType");

    /**
     * Multi-farm charts get a special treatment.
     * We will compare farms so the color will be determined by the series type.
     * @type {boolean}
     */
    const isMultiFarm = _.size(_.uniq(_.flatMap(m, "farmIds"))) > 1;

    /**
     * Check if there are multiple series types
     * @type {boolean}
     */
    const isMultiType = _.size(_.compact(seriesTypes)) > 1;

    return isMultiType && !isMultiFarm;
  }, [m]);

  /**
   * Update the palette type based on the overlay and the metadata
   */
  useDeepCompareEffect(() => {
    if (hasOverlays && paletteType !== ChartSeriesType.overlay) {
      setPaletteType(ChartSeriesType.overlay);
    } else {
      const [seriesType] = _.uniq(_.compact(_.map(m, "seriesType")));

      setPaletteType(seriesType);
    }
  }, [hasOverlays, m]);

  /**
   * Get the metadata for the main chart
   * @type {() => any}
   */
  const getMeta = useDeepCompareCallback(() => {
    const [main] = entities;

    return _.get(m, [main.id], {} as DataPointsMeta);
  }, [m, entities]);

  /**
   * Get the chart type from the metadata
   * @type {ChartType}
   */
  const chartType: ChartType | undefined = _.get(getMeta(), ["type"]);

  /**
   * Count the number of items in the legend
   * @type {number}
   */
  const legendSize = _.size(series);

  const chartTypes = _.map(_.keys(m), (key) => {
    const [, chartType] = key.split("-");

    return chartType as ChartType;
  });

  /**
   * Tell if the chart is a stacked chart
   * @type {boolean}
   */
  const isStack = _.every(chartTypes, (chartType) => {
    return [ChartType.stackedColumn, ChartType.stackedPercentColumn].includes(chartType);
  });

  /**
   * Tell if the chart is a percent chart
   * @type {boolean}
   */
  const isPercent = _.every(chartTypes, (chartType) => {
    return chartType === ChartType.stackedPercentColumn;
  });

  /**
   * Tell if the chart is a basic bar chart
   * @type {boolean}
   */
  const isBasicBar = _.every(chartTypes, (chartType) => {
    return chartType === ChartType.basicBar;
  });

  /**
   * Tell if the chart should put a tooltip in the title
   * @type {boolean}
   */
  const hasTooltipTitle = !isBasicBar;

  /**
   * Tell if the chart may be a grouped column chart
   * @type {boolean}
   */
  const isGroup =
    processor.current.mayGroup() ||
    _.every(chartTypes, (chartType) => {
      return chartType === ChartType.groupedColumn;
    });

  const [main] = entities;
  const { axis, yAxis } = _.get(m, [main.id], {} as DataPointsMeta);

  /**
   * Get the y-axis scale from the metadata
   * @type {string}
   */
  const yScale: string = _.get(yAxis, ["scale"]);

  debugger;

  /**
   * Column chart config
   * @type {{yAxis: {min: number | undefined, max: number | undefined, line: {style: {stroke: string, lineWidth: number}}, label: {formatter: (value) => number}, tickLine: {length: number, style: {stroke: string, lineWidth: number}}}, xAxis: {line: {style: {stroke: string, lineWidth: number}}, label: {formatter: (value) => string | undefined, offset: number}, nice: boolean, tickLine: {length: number, style: {stroke: string, lineWidth: number}}}, xField: string, color: ({seriesField}: {seriesField: any}) => string, data: any[], yField: string, tooltip: {shared: boolean, customContent: (_title, content) => HTMLElement, showTitle: boolean}, seriesField: string, intervalPadding: number, isStack: boolean}}
   */
  const config: ColumnConfig = useDeepCompareMemo(
    () => ({
      color: ({ seriesField }) => {
        return hslToHex(getColor(void 0, seriesField));
      },
      /**
       * turn off the legends if there is only one series
       */
      legend: isBasicBar ? false : legendSize > 1 ? commonConfig.legend : false,
      data,
      intervalPadding: isGroup ? void 0 : 8,
      isStack,
      isPercent,
      isGroup,
      seriesField: "seriesField",
      groupField: "groupField",
      tooltip: {
        shared: legendSize <= 3,
        containerTpl: `
          <div class="g2-tooltip g2-columnChart-tooltip">
            <ul class="g2-tooltip-list"></ul>
            ${hasTooltipTitle ? `<div class="g2-tooltip-title"></div>` : ""}
          </div>
        `,
        showTitle: hasTooltipTitle,
        title: (value: string, { x }: Datum) => {
          /**
           * The main chart meta will stay dominant for the tooltip.
           * We don't want mixed values.
           */
          const meta = getMeta();

          const label = formatXLabel(
            x,
            { type: meta.xType, bucketSize },
            { mayLocalise: false, mayConvertToUTC: false }
          );

          return isDebugMode() ? `${label} (${x})` : label;
        },
        formatter: ({ x, y, seriesField }: Datum) => {
          const metricName = _.get(_.find(m, ["variantId", seriesField]), ["title"]);

          return {
            name: _.get(
              series,
              [seriesField],
              metricName
                ? t<string>(displayName(metricName), {
                    case: null,
                    variant: null,
                    makeDefaultValue: true
                  })
                : seriesField
            ),
            value: isPercent ? Units.format(y * 100, "percentage", void 0, { precision: 2 }) : y
          };
        }
      },
      meta:
        chartType === ChartType.basicBar
          ? void 0
          : {
              x: {
                sync: true,
                type: "timeCat"
              }
            },
      xAxis: {
        label: {
          formatter: (value) => {
            /**
             * The main chart meta will stay dominant for the tooltip.
             * We don't want mixed values.
             */
            const meta = getMeta();

            const label = formatXLabel(
              value,
              { type: meta.xType, bucketSize },
              { mayLocalise: false, mayConvertToUTC: false }
            );

            return isDebugMode() ? `${label} (${value})` : label;
          },
          offset: 10,
          autoRotate: true,
          autoHide: false,
          autoEllipsis: true
        },
        line: {
          style: {
            lineWidth: 0.5,
            stroke: "#000000"
          }
        },
        nice: true,
        tickLine: {
          length: 5,
          style: {
            lineWidth: 1,
            stroke: "#000000"
          }
        }
      },
      yAxis: {
        type: yScale === "logarithmic" ? "log" : void 0,
        title: {
          text: _.get(axis, ["y", "title"], "")
        },
        label: {
          formatter: (value) => {
            return _.round(parseFloat(value), 2);
          }
        },
        line: {
          style: {
            lineWidth: 0.5,
            stroke: "#000000"
          }
        },
        max: isPercent ? void 0 : getStackedMax(data, "x", "y"),
        min: isPercent ? void 0 : 0,
        tickLine: {
          style: {
            lineWidth: 1,
            stroke: "#000000"
          },
          length: 5
        }
      },
      xField: "x",
      yField: "y"
    }),
    [data, series, axis, commonConfig, legendSize]
  );

  return useDeepCompareMemo(
    () => ({
      config: _.mergeWith({}, contextConfig, commonConfig, config, (objValue, srcValue) => {
        if (_.isFunction(objValue) && _.isFunction(srcValue)) {
          return _.over([objValue, srcValue]);
        }
      })
    }),
    [config, commonConfig, contextConfig]
  );
};
